// Cloud persistence API client

// Normalize descriptor to kernel rules (preserve graph semantics):
// - Preserve `START` sentinel as a valid source; never rewrite or drop it
// - Preserve `END` sentinel as a valid target; never map it to a completion id
// - Normalize any `start_*` variants to the literal `START`
// - Preserve conditions verbatim and priorities (non-numeric treated as 0)
function normalizeDescriptorForKernel(descriptor) {
  if (!descriptor || typeof descriptor !== 'object') return descriptor;
  const d = { ...descriptor };
  const qs = Array.isArray(d.questions) ? d.questions : [];
  const flow = Array.isArray(d.flow) ? d.flow : [];
  if (qs.length === 0 || flow.length === 0) return d;
  const validIds = new Set(qs.map(q => String(q.question_id || '').trim()).filter(Boolean));
  // No special handling of a concrete intro/completion node ids here; flow uses START/END sentinels

  let edges = [];
  for (const e of flow) {
    let from = String(e.from || '').trim();
    let to = String(e.to || '').trim();
    // Normalize any accidental 'start' label variants to the literal START token
    const isStartVariant = (s) => /^start(?:$|[_-])/i.test(String(s || ''));
    if (isStartVariant(from)) from = 'START';
    if (isStartVariant(to)) to = 'START';
    let condition = (e.condition != null) ? String(e.condition) : '';
    // Validation/migration: treat empty-string comparisons as unconditional
    if (/==\s*""/.test(condition)) condition = '';
    // Auto-migrate legacy isDefault flag
    const pNum = Number(e.priority);
    let priority = Number.isFinite(pNum) ? pNum : 0;
    if (e.isDefault === true && !condition) {
      condition = '';
      // Set a high priority; unconditional edges will be normalized to max+1 per source below
      priority = Math.max(priority, 999);
    }
    edges.push({ from, to, condition, priority });
  }

  // Prune any edges that reference unknown ids, but keep sentinel START/END
  edges = edges.filter(e => {
    const okFrom = (e.from === 'START') || validIds.has(e.from);
    const okTo = (e.to === 'END') || validIds.has(e.to);
    return okFrom && okTo;
  });

  // For each from-node, ensure unconditional edges (blank condition) are sorted last by bumping priority to max+1
  const byFrom = new Map();
  for (const ed of edges) {
    if (!byFrom.has(ed.from)) byFrom.set(ed.from, []);
    byFrom.get(ed.from).push(ed);
  }
  for (const [, list] of byFrom.entries()) {
    const maxP = list.reduce((m, e) => Math.max(m, Number(e.priority || 0)), 0);
    // multiple blank-condition edges with tied priorities: keep one at max+1, flagging others by increasing further
    let bump = 1;
    list.filter(e => String(e.condition || '').trim() === '').forEach((e, idx) => {
      e.priority = maxP + bump;
      bump += 1;
    });
  }

  d.flow = edges;
  return d;
}

export const getKernelApiBase = () => {
  const base = process.env.REACT_APP_SAM_DBAM_URL;
  if (!base) throw new Error("REACT_APP_SAM_DBAM_URL is not set");
  return `${base.replace(/\/+$/, "")}/dbam/v1`;
};

// ---- Org context for account-scoped requests ----
// Set once after login so all API calls include the X-Org-ID header.
let __orgContext = { orgId: null, userId: null };

/**
 * Set the org context for all subsequent API calls.
 * Call this after the user profile is loaded (e.g. from AuthContext).
 */
export function setOrgContext(orgId, userId) {
  __orgContext = { orgId: orgId || null, userId: userId || null };
}

/**
 * Get the current org context.
 */
export function getOrgContext() {
  return { ...__orgContext };
}

/**
 * Build org-context headers for direct fetch calls.
 * Use this when not going through httpRequest (which injects them automatically).
 */
function orgHeaders(extra = {}) {
  const h = { ...extra };
  if (__orgContext.orgId) h['X-Org-ID'] = __orgContext.orgId;
  if (__orgContext.userId) h['X-User-ID'] = __orgContext.userId;
  return h;
}

/**
 * Unwrap sam-dbam response format.
 * sam-dbam wraps responses as {data: ..., meta: {...}}
 * For lists: {data: [...], meta: {...}} or {data: {data: [...], pagination: {...}}, meta: {...}}
 * For singles: {data: {...}, meta: {...}}
 * This function extracts the actual data.
 */
function unwrapSamDbamResponse(resp, expectArray = false) {
  if (!resp || typeof resp !== 'object') return resp;

  // If response has data property, unwrap it
  if (resp.data !== undefined) {
    const inner = resp.data;
    // Nested structure: {data: {data: [...], pagination: {...}}, meta: {...}}
    if (inner && typeof inner === 'object' && Array.isArray(inner.data)) {
      return inner.data;
    }
    // Direct structure: {data: [...], meta: {...}} or {data: {...}, meta: {...}}
    if (expectArray && Array.isArray(inner)) {
      return inner;
    }
    return inner;
  }

  // Already unwrapped or different format
  return resp;
}

/**
 * Retry wrapper around httpRequest for cold-start resilience.
 * Retries on 5xx errors and network failures with exponential backoff.
 * Only use for idempotent (GET) or safe-to-retry operations.
 */
async function httpRequestWithRetry(url, options = {}, httpOpts = {}, { retries = 3, baseDelayMs = 500, factor = 2 } = {}) {
  let lastErr = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await httpRequest(url, options, httpOpts);
    } catch (err) {
      lastErr = err;
      const status = err && err.status;
      const isRetryable = (status >= 500) || err.code === 'ETIMEOUT' || !status; // 5xx, timeout, or network error
      if (!isRetryable || attempt >= retries) throw err;
      const delay = baseDelayMs * Math.pow(factor, attempt);
      await new Promise(r => setTimeout(r, delay));
    }
  }
  throw lastErr;
}

// ---- Phase 0 Utilities (non-breaking; no UI wiring) ----

/**
 * Generic HTTP request with timeout and response-type handling.
 * Returns parsed JSON for responseType='json'; raw text for 'text'/'csv'.
 */
async function httpRequest(url, options = {}, { timeoutMs = 10000, responseType = 'json' } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    // Inject org context headers so the backend can scope results
    // to the caller's organization.
    const orgHeaders = {};
    if (__orgContext.orgId) {
      orgHeaders['X-Org-ID'] = __orgContext.orgId;
    }
    if (__orgContext.userId) {
      orgHeaders['X-User-ID'] = __orgContext.userId;
    }
    const mergedOptions = {
      ...options,
      signal: controller.signal,
      headers: { ...orgHeaders, ...(options.headers || {}) },
    };
    const resp = await fetch(url, mergedOptions);
    const ok = resp.ok;
    const status = resp.status;
    if (!ok) {
      let errText = '';
      try { errText = await resp.text(); } catch (_) { }
      // Try to extract structured error code if JSON
      let code = null;
      try {
        const parsed = JSON.parse(errText);
        if (parsed && parsed.error && (parsed.error.code || parsed.code)) {
          code = parsed.error.code || parsed.code;
        }
      } catch (_) { /* not JSON */ }
      const message = errText || resp.statusText || 'Request failed';
      const error = new Error(message);
      error.status = status;
      if (status === 403 && (code === 'CORS_POLICY_VIOLATION' || /CORS_POLICY_VIOLATION/.test(message))) {
        error.code = 'CORS_POLICY_VIOLATION';
      } else if (code) {
        error.code = code;
      }
      throw error;
    }
    if (responseType === 'json') {
      return await resp.json();
    }
    // 'text' or 'csv'
    return await resp.text();
  } catch (err) {
    // Normalize AbortError into a clearer timeout message
    if (err && (err.name === 'AbortError' || /aborted/i.test(String(err.message)))) {
      const timeoutError = new Error(`Request timed out after ${timeoutMs} ms`);
      timeoutError.code = 'ETIMEOUT';
      throw timeoutError;
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Get the sam-dbam API base (includes /dbam/v1).
 */
function getKernelApiV5Base() {
  return getKernelApiBase().replace(/\/$/, '');
}

let __validatedKernelBaseCache = { base: null, validatedAt: 0 };

/**
 * Resolve and validate the sam-dbam API base by hitting health endpoint.
 * Caches a successful validation for 60 seconds.
 */
export async function getValidatedKernelApiBase({ timeoutMs = 1500 } = {}) {
  const now = Date.now();
  if (__validatedKernelBaseCache.base && now - __validatedKernelBaseCache.validatedAt < 60000) {
    return __validatedKernelBaseCache.base;
  }
  const base = getKernelApiBase().replace(/\/$/, '');
  const tryUrls = [base + '/health'];
  let ok = false;
  for (const u of tryUrls) {
    try {
      await httpRequest(u, { method: 'GET' }, { timeoutMs, responseType: 'json' });
      ok = true;
      break;
    } catch (_) {
      // continue
    }
  }
  if (!ok) {
    throw new Error('Kernel health check failed; cannot validate API base');
  }
  __validatedKernelBaseCache = { base, validatedAt: now };
  return base;
}

/**
 * Fetch sam-dbam health status.
 */
export async function fetchKernelHealth({ timeoutMs = 1500 } = {}) {
  const base = getKernelApiBase().replace(/\/$/, '');
  const healthUrl = base + '/health';
  try {
    const body = await httpRequest(healthUrl, { method: 'GET' }, { timeoutMs, responseType: 'json' });
    return { ok: true, status: 200, body };
  } catch (e) {
    return { ok: false, status: e.status || 0, error: e.message };
  }
}

/**
 * Fetch analyzer status; 404 means analyzer unavailable.
 */
export async function fetchAnalyzerStatus({ timeoutMs = 2000 } = {}) {
  const base = getKernelApiV5Base();
  const url = `${base}/analyzer/status`;
  try {
    const body = await httpRequest(url, { method: 'GET' }, { timeoutMs, responseType: 'json' });
    return { available: true, body };
  } catch (e) {
    if (e && e.status === 404) {
      return { available: false, body: null };
    }
    return { available: false, error: e.message, status: e.status || 0 };
  }
}

/**
 * Analyzer status with exponential backoff on transient failures.
 */
export async function fetchAnalyzerStatusWithBackoff({
  timeoutMs = 2000,
  retries = 3,
  baseDelayMs = 500,
  factor = 2
} = {}) {
  let attempt = 0;
  let delay = baseDelayMs;
  while (attempt <= retries) {
    const result = await fetchAnalyzerStatus({ timeoutMs });
    if (result.available || result.status === 404) {
      return result;
    }
    attempt += 1;
    if (attempt > retries) break;
    await new Promise(r => setTimeout(r, delay));
    delay = Math.min(delay * factor, 8000);
  }
  return { available: false, error: 'Analyzer status failed after retries' };
}

/** Fetch sessions for a survey from sam-dbam **/
export async function fetchAnalyzerSessions(surveyId, { limit = 50, offset = 0, timeoutMs = 3000 } = {}) {
  if (!surveyId) return [];
  const base = getKernelApiV5Base();
  const url = `${base}/surveys/${encodeURIComponent(surveyId)}/sessions?limit=${limit}&offset=${offset}`;
  try {
    const resp = await httpRequest(url, { method: 'GET' }, { timeoutMs, responseType: 'json' });
    const rows = unwrapSamDbamResponse(resp, true);
    return Array.isArray(rows) ? rows : [];
  } catch (e) {
    if (e.status === 404) return [];
    throw e;
  }
}

/**
 * Transform raw analyzer_events into exchange-shaped objects for TimelineView.
 * Groups events by exchange_seq and extracts human/AI content from payloads.
 */
function eventsToExchanges(events) {
  if (!Array.isArray(events) || events.length === 0) return { exchanges: [], prompts: {} };
  const bySeq = {};
  const promptsBySeq = {};
  for (const ev of events) {
    const seq = ev.exchange_seq ?? 0;
    if (!bySeq[seq]) bySeq[seq] = { exchange_seq: seq, question_id: ev.question_id || null, human: [], ai_responses: [] };
    const cls = String(ev.class || '').toLowerCase();
    const payload = ev.payload || {};
    if (cls === 'human' || cls === 'human_utterance' || cls === 'response' || cls === 'asr_result' || cls === 'asr') {
      const text = payload.text || payload.utterance || payload.content || payload.raw || (typeof payload === 'string' ? payload : '');
      if (text) bySeq[seq].human.push(String(text));
    } else if (cls === 'ai' || cls === 'ai_response' || cls === 'sam' || cls === 'conversational') {
      bySeq[seq].ai_responses.push({
        text: payload.text || payload.utterance || payload.content || payload.conversationalResponse || '',
        type: payload.type || payload.evaluationType || null,
        nextInstruction: payload.nextInstruction || null,
        shouldAdvanceSurvey: payload.shouldAdvanceSurvey ?? null,
        extractedData: payload.extractedData || null,
      });
    } else if (cls === 'nap' || cls === 'normalized_ai_payload') {
      bySeq[seq].ai_responses.push({
        text: payload.conversationalResponse || payload.text || '',
        type: payload.evaluationType || payload.type || null,
        nextInstruction: payload.nextInstruction || null,
        shouldAdvanceSurvey: payload.shouldAdvanceSurvey ?? null,
        extractedData: payload.extractedData || null,
      });
    } else if (cls === 'ai_prompt') {
      if (!promptsBySeq[seq]) promptsBySeq[seq] = [];
      promptsBySeq[seq].push({
        exchange_seq: seq,
        prompt_type: payload.prompt_type || 'hybrid',
        bytes: payload.bytes || (payload.prompt_text ? payload.prompt_text.length : 0),
        prompt_text: payload.prompt_text || '',
      });
    }
    // Prefer non-null question_id
    if (ev.question_id && !bySeq[seq].question_id) bySeq[seq].question_id = ev.question_id;
  }
  return {
    exchanges: Object.values(bySeq).sort((a, b) => a.exchange_seq - b.exchange_seq),
    prompts: promptsBySeq,
  };
}

export async function fetchAnalyzerExchanges(sessionId, { timeoutMs = 3000 } = {}) {
  if (!sessionId) return { exchanges: [], prompts: {} };
  const base = getKernelApiV5Base();
  const url = `${base}/sessions/${encodeURIComponent(sessionId)}/analyzer-events`;
  try {
    const resp = await httpRequest(url, { method: 'GET' }, { timeoutMs, responseType: 'json' });
    const events = unwrapSamDbamResponse(resp, true);
    return eventsToExchanges(Array.isArray(events) ? events : []);
  } catch (e) {
    if (e.status === 404) return { exchanges: [], prompts: {} };
    throw e;
  }
}

/**
 * Bulk-fetch analyzer exchanges for multiple sessions in a single HTTP request.
 * Returns a map of { [sessionId]: { exchanges, prompts } }.
 */
export async function fetchAnalyzerExchangesBulk(sessionIds, { timeoutMs = 10000 } = {}) {
  if (!sessionIds || sessionIds.length === 0) return {};
  const base = getKernelApiV5Base();
  const url = `${base}/sessions/analyzer-events/bulk`;
  try {
    const resp = await httpRequest(
      url,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ session_ids: sessionIds }) },
      { timeoutMs, responseType: 'json' }
    );
    // Backend returns { data: { [sessionId]: [events...] } }
    const bySession = unwrapSamDbamResponse(resp, false) || {};
    const result = {};
    for (const sid of sessionIds) {
      const events = Array.isArray(bySession[sid]) ? bySession[sid] : [];
      result[sid] = eventsToExchanges(events);
    }
    return result;
  } catch (e) {
    if (e.status === 404) return {};
    throw e;
  }
}


export async function fetchAnalyzerPrompts(sessionId, { timeoutMs = 3000 } = {}) {
  if (!sessionId) return [];
  const base = getKernelApiV5Base();
  const url = `${base}/analyzer/sessions/${encodeURIComponent(sessionId)}/prompts`;
  try {
    const rows = await httpRequest(url, { method: 'GET' }, { timeoutMs, responseType: 'json' });
    return Array.isArray(rows) ? rows : [];
  } catch (e) {
    if (e.status === 404) return [];
    throw e;
  }
}

export async function fetchAnalyzerAI(sessionId, { timeoutMs = 3000 } = {}) {
  if (!sessionId) return [];
  const base = getKernelApiV5Base();
  const url = `${base}/analyzer/sessions/${encodeURIComponent(sessionId)}/ai`;
  try {
    const rows = await httpRequest(url, { method: 'GET' }, { timeoutMs, responseType: 'json' });
    return Array.isArray(rows) ? rows : [];
  } catch (e) {
    if (e.status === 404) return [];
    throw e;
  }
}

export async function deleteAnalyzerSession(sessionId, { timeoutMs = 4000 } = {}) {
  if (!sessionId) throw new Error('sessionId is required');
  // Best-effort GCS audio cleanup before session deletion
  try {
    const responses = await fetchSessionResponses(sessionId, { timeoutMs: 4000 });
    const audioPaths = (Array.isArray(responses) ? responses : [])
      .map(r => r.audio_storage_path)
      .filter(p => p && typeof p === 'string' && p.startsWith('gs://'));
    if (audioPaths.length > 0) {
      console.log(`[deleteSession] Cleaning up ${audioPaths.length} audio recording(s) for session ${sessionId}`);
      await Promise.allSettled(audioPaths.map(p => deleteAudioRecording(p)));
    }
  } catch (audioErr) {
    console.warn('[deleteSession] Audio cleanup failed (non-blocking):', audioErr.message);
  }
  const base = getKernelApiBase();
  const url = `${base}/sessions/${encodeURIComponent(sessionId)}`;
  await httpRequest(url, { method: 'DELETE' }, { timeoutMs, responseType: 'text' });
  return { ok: true };
}

export async function createSurveyInCloud({ name, descriptor, initialStatus, designerGraph, designerPayload }) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/surveys`;
  const cleaned = normalizeDescriptorForKernel(descriptor);

  let resp;
  try {
    const body = { name, descriptor: cleaned, initial_status: initialStatus, designer_graph: designerGraph, designer_payload: designerPayload };
    // Attach org ownership so the survey is scoped to the caller's org
    if (__orgContext.orgId) body.org_id = __orgContext.orgId;
    resp = await fetch(url, {
      method: 'POST',
      headers: orgHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body)
    });
  } catch (fetchError) {
    throw new Error(`Failed to connect to server: ${fetchError.message}. Check if the kernel service is running and CORS is configured properly.`);
  }

  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    if (resp.status >= 500) {
      throw new Error(`Server error (${resp.status}): ${text || 'Internal server error'}`);
    }
    throw new Error(`Survey create failed (${resp.status}): ${text || resp.statusText}`);
  }
  return unwrapSamDbamResponse(await resp.json());
}

export async function createSurveyVersionInCloud({ surveyId, descriptor, status, publish, notes, designerGraph, designerPayload }) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/surveys/${encodeURIComponent(surveyId)}/versions`;
  const cleaned = normalizeDescriptorForKernel(descriptor);
  const resp = await fetch(url, {
    method: 'POST',
    headers: orgHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ descriptor: cleaned, status, publish, notes, designer_graph: designerGraph, designer_payload: designerPayload })
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Version create failed (${resp.status}): ${text || resp.statusText}`);
  }
  return unwrapSamDbamResponse(await resp.json());
}

export async function updateSurveyVersionInCloud({ surveyId, versionNumber, descriptor, status, notes, designerPayload, etag }) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/surveys/${encodeURIComponent(surveyId)}/versions/${encodeURIComponent(versionNumber)}`;
  const headers = orgHeaders({ 'Content-Type': 'application/json' });
  if (etag) headers['If-Match'] = `"${etag}"`;
  const cleaned = descriptor ? normalizeDescriptorForKernel(descriptor) : undefined;

  let resp;
  try {
    resp = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ descriptor: cleaned, status, notes, designer_graph: undefined, designer_payload: designerPayload })
    });
  } catch (fetchError) {
    // Network or CORS errors
    throw new Error(`Failed to connect to server: ${fetchError.message}. Check if the kernel service is running and CORS is configured properly.`);
  }

  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    if (resp.status === 412) {
      throw new Error('Version update failed: Survey was modified by another user. Please refresh and try again.');
    }
    if (resp.status === 404) {
      throw new Error('Survey or version not found. It may have been deleted.');
    }
    if (resp.status >= 500) {
      throw new Error(`Server error (${resp.status}): ${text || 'Internal server error'}`);
    }
    throw new Error(`Version update failed (${resp.status}): ${text || resp.statusText}`);
  }
  return unwrapSamDbamResponse(await resp.json());
}

export async function getSurveySummaryFromCloud(surveyId) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/surveys/${encodeURIComponent(surveyId)}`;
  const resp = await fetch(url, { headers: orgHeaders() });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Fetch survey failed (${resp.status}): ${text || resp.statusText}`);
  }
  return unwrapSamDbamResponse(await resp.json());
}

export async function getSurveyVersionFromCloud(surveyId, versionNumber) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/surveys/${encodeURIComponent(surveyId)}/versions/${encodeURIComponent(versionNumber)}`;
  const resp = await fetch(url, { headers: orgHeaders() });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Fetch version failed (${resp.status}): ${text || resp.statusText}`);
  }
  const data = await resp.json();
  const unwrapped = unwrapSamDbamResponse(data);
  const etag = unwrapped?.etag || resp.headers.get('ETag') || null;
  return { data: unwrapped, etag };
}

export async function listSurveysFromCloud({ limit = 50, offset = 0, search = '', orgId = '' } = {}) {
  const apiBase = getKernelApiBase();
  let url = `${apiBase}/surveys?limit=${encodeURIComponent(limit)}&offset=${encodeURIComponent(offset)}&search=${encodeURIComponent(search)}`;
  if (orgId) url += `&org_id=${encodeURIComponent(orgId)}`;
  const json = await httpRequestWithRetry(url, { method: 'GET' }, { timeoutMs: 10000 });
  const surveys = unwrapSamDbamResponse(json, true);
  // Map 'id' to 'survey_id' for Console UI compatibility
  // Return {items: [...]} format expected by App.js
  return {
    items: (surveys || []).map(s => ({
      ...s,
      survey_id: s.id || s.survey_id,
    }))
  };
}

// --- Console helpers ---
export async function deploySurveyInCloud({ surveyId, customSlug }) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/deployments`;
  // Use custom slug if provided, otherwise auto-generate
  const slug = customSlug
    ? customSlug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').slice(0, 64)
    : `deploy-${surveyId.slice(0, 8)}-${Date.now()}`;
  const headers = { 'Content-Type': 'application/json' };
  const body = {
    survey_id: surveyId,
    deploy_slug: slug,
    is_open: true,
  };
  // Include org context so the deployment is scoped to the caller's org
  if (__orgContext.orgId) {
    headers['X-Org-ID'] = __orgContext.orgId;
    body.org_id = __orgContext.orgId;
  }
  if (__orgContext.userId) {
    headers['X-User-ID'] = __orgContext.userId;
    body.user_id = __orgContext.userId;
  }
  const resp = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    // A survey the kernel cannot execute comes back as 422 SURVEY_NOT_EXECUTABLE
    // with a message written for the author. Show that sentence on its own —
    // wrapping it in "Deploy failed (422): {json}" buries the one useful line.
    let parsed = null;
    try { parsed = JSON.parse(text); } catch (_) { /* not a JSON envelope */ }
    const code = parsed?.error?.code;
    const message = parsed?.error?.message;
    if (code === 'SURVEY_NOT_EXECUTABLE' && message) throw new Error(message);
    if (message) throw new Error(`Deploy failed: ${message}`);
    throw new Error(`Deploy failed (${resp.status}): ${text || resp.statusText}`);
  }
  const data = await resp.json();
  // Return in expected format
  return data.data || data;
}

// --- Survey sharing between accounts (migration 019) ---

// Send a copy of a survey to another account by email. Server enforces the
// tier rule (paid-only sending; superuser exemptions in both directions).
export async function shareSurveyInCloud({ surveyId, toEmail, message }) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/surveys/${encodeURIComponent(surveyId)}/shares`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: orgHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ to_email: toEmail, message: message || '' })
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Share failed (${resp.status}): ${text || resp.statusText}`);
  }
  return unwrapSamDbamResponse(await resp.json());
}

// List shares for the current org. direction: 'incoming' | 'outgoing'.
export async function listSharesFromCloud({ direction = 'incoming' } = {}) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/shares?direction=${encodeURIComponent(direction)}`;
  const resp = await fetch(url, { headers: orgHeaders() });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`List shares failed (${resp.status}): ${text || resp.statusText}`);
  }
  return unwrapSamDbamResponse(await resp.json(), true) || [];
}

// Accept an incoming share — creates a draft copy in this account.
export async function acceptShareInCloud({ shareId }) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/shares/${encodeURIComponent(shareId)}/accept`;
  const resp = await fetch(url, { method: 'POST', headers: orgHeaders() });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Accept share failed (${resp.status}): ${text || resp.statusText}`);
  }
  return unwrapSamDbamResponse(await resp.json());
}

// Decline an incoming share.
export async function declineShareInCloud({ shareId }) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/shares/${encodeURIComponent(shareId)}/decline`;
  const resp = await fetch(url, { method: 'POST', headers: orgHeaders() });
  if (!resp.ok && resp.status !== 204) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Decline share failed (${resp.status}): ${text || resp.statusText}`);
  }
  return true;
}

// Revoke a pending outgoing share.
export async function revokeShareInCloud({ shareId }) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/shares/${encodeURIComponent(shareId)}`;
  const resp = await fetch(url, { method: 'DELETE', headers: orgHeaders() });
  if (!resp.ok && resp.status !== 204) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Revoke share failed (${resp.status}): ${text || resp.statusText}`);
  }
  return true;
}

// Assign a custom URL slug to an existing deployment (console "Custom" button).
// The server rejects with 409 DUPLICATE_SLUG if another open deployment holds it.
export async function updateDeploymentSlugInCloud({ deploymentId, slug }) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/deployments/${encodeURIComponent(deploymentId)}`;
  const resp = await fetch(url, {
    method: 'PATCH',
    headers: orgHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ deploy_slug: slug })
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Set custom slug failed (${resp.status}): ${text || resp.statusText}`);
  }
  return unwrapSamDbamResponse(await resp.json());
}

// ---- Survey lifecycle transitions (WP3) -----------------------------------
//
// Server-enforced transitions under /dbam/v1/deployments/{id}/*. These replace
// the console's old localStorage-only staging and the soft `is_open:false`
// undeploy. The survey lifecycle state (surveys.status) is written server-side;
// the console re-reads it via listSurveysFromCloud after each transition.
//
// A rejected transition returns 409 { error: { code:
// "INVALID_LIFECYCLE_TRANSITION", message } }; we surface the server message
// and stamp err.code so callers can branch on it.
async function lifecyclePost(deploymentId, action, body) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/deployments/${encodeURIComponent(deploymentId)}/${action}`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: orgHeaders({ 'Content-Type': 'application/json' }),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!resp.ok) {
    throw await lifecycleError(resp, `${action} failed (${resp.status})`);
  }
  // stage/unstage/send → { deployment, survey_status }
  // undeploy → UndeployResult { closed, requires_confirmation, counts, ... }
  return unwrapSamDbamResponse(await resp.json());
}

// lifecycleError turns a failed response into an Error the UI can show as it is.
//
// A plan limit refusal arrives as 403 UPGRADE_REQUIRED. Its message is already
// written for the person using the product, so it is used unchanged. The limit
// numbers ride along on the error for any caller that wants to build its own
// wording. isLimitRefusal marks it so callers can tell "your plan says no" from
// "something broke".
async function lifecycleError(resp, fallbackMessage) {
  let payload = null;
  try { payload = await resp.json(); } catch (_) { /* non-JSON body */ }
  const code = payload?.error?.code || payload?.code || null;
  const message = payload?.error?.message || payload?.message || fallbackMessage;
  const err = new Error(message);
  err.status = resp.status;
  if (code) err.code = code;
  // A send refused because the author's own AI model is not answering. The
  // message is already written for them and says what to do, so it is used as
  // it is. The probe result rides along for any caller that wants to show the
  // three checks the way the Account panel does.
  if (code === 'BYOM_PREFLIGHT_FAILED') {
    err.isCustomModelFailure = true;
    err.test = payload?.test || null;
  }
  if (code === 'UPGRADE_REQUIRED') {
    err.isLimitRefusal = true;
    err.limit = payload?.limit;
    err.actual = payload?.actual;
    err.tier = payload?.tier;
    err.limitKey = payload?.limit_key;
    err.resetsAt = payload?.resets_at || null;
  }
  return err;
}

// Stage: deployed → staged (drag a deployed survey into the Staging Area).
export async function stageDeploymentInCloud({ deploymentId }) {
  return lifecyclePost(deploymentId, 'stage');
}

// Unstage: staged → deployed ('x' a survey out of the Staging Area).
export async function unstageDeploymentInCloud({ deploymentId }) {
  return lifecyclePost(deploymentId, 'unstage');
}

// Send confirmation is the sole path that creates the permanent recipient list.
// It moves staged → sent before any ChannelGate request and derives sent_count
// itself.
//
// It takes no recipient list. sam-dbam reads the staged list it already holds,
// which staging built under the plan's recipient cap. That is why nothing here
// can send to more people than the plan allows.
export async function sendDeploymentInCloud({ deploymentId }) {
  if (!deploymentId) {
    throw new Error('A deployment is required');
  }
  const data = await lifecyclePost(deploymentId, 'send');
  if (!data || !data.deployment || !Array.isArray(data.recipients)) {
    throw new Error('Send confirmation returned an invalid recipient list');
  }
  return data;
}

// ---- Email settings send semaphore ----------------------------------------
// Console still runs the short recipient loop in this tab. A server-held lease
// makes the organization email setup read-only for that interval, so one batch
// cannot change sender halfway through. The lease expires if the tab disappears.
export async function acquireEmailSendLease({ holder, deploymentId = null, ttlSeconds = 600 }) {
  const url = `${getKernelApiBase()}/channels/email/send-leases`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: orgHeaders({ 'Content-Type': 'application/json' }),
    credentials: 'include',
    body: JSON.stringify({ holder, deployment_id: deploymentId, ttl_seconds: ttlSeconds }),
  });
  if (!resp.ok) throw await lifecycleError(resp, `Could not lock email settings for this send (${resp.status})`);
  return unwrapSamDbamResponse(await resp.json());
}

export async function releaseEmailSendLease(leaseId) {
  if (!leaseId) return;
  const url = `${getKernelApiBase()}/channels/email/send-leases/${encodeURIComponent(leaseId)}`;
  const resp = await fetch(url, {
    method: 'DELETE',
    headers: orgHeaders(),
    credentials: 'include',
  });
  if (!resp.ok && resp.status !== 404) {
    throw await lifecycleError(resp, `Could not release the email settings lock (${resp.status})`);
  }
}

// ---- The staged recipients --------------------------------
//
// This selection used to live only in this browser's localStorage. sam-dbam
// never saw it, so it could not hold it to the plan's per-survey cap. It now
// lives in sam-dbam and these three calls are the only way it changes.
//
// No deployment id is involved. The staged recipients belongs to the account and
// applies to every survey in the staging area, and the author builds it before
// staging a survey as often as after.
//
// All three return the whole selection, so the caller never has to guess what
// it now holds. addStagingAreaRecipient throws an error with isLimitRefusal set
// when the plan's cap is reached; its message is ready to show.

async function stagingAreaRequest({ method = 'GET', path = '', body } = {}) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/staging-area/recipients${path}`;
  const resp = await fetch(url, {
    method,
    headers: orgHeaders({ 'Content-Type': 'application/json' }),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!resp.ok) {
    throw await lifecycleError(resp, `Could not update the staged recipients (${resp.status})`);
  }
  const data = unwrapSamDbamResponse(await resp.json());
  return {
    recipients: Array.isArray(data?.recipients) ? data.recipients : [],
    dispatch_mode: data?.dispatch_mode || 'channel_response',
    deployment_id: data?.deployment_id || null,
    direct_channel: data?.direct_channel || null,
    direct_sender_binding_id: data?.direct_sender_binding_id || null,
    crm_offered_channels: Array.isArray(data?.crm_offered_channels) ? data.crm_offered_channels : [],
    crm_email_binding_id: data?.crm_email_binding_id || null,
    revision: Number(data?.revision) || 0,
    reconciled: Boolean(data?.reconciled),
    reconciliation: data?.reconciliation || '',
    // Rolling compatibility for callers that still read `channel`.
    channel: data?.direct_channel || '',
  };
}

export async function listStagingAreaRecipients() {
  return (await stagingAreaRequest()).recipients;
}

// listStagingArea returns the selection AND the channel context in one read.
// The staging hook uses this; listStagingAreaRecipients above stays for
// callers that only care about the people.
export async function listStagingArea() {
  return stagingAreaRequest();
}

// setStagingAreaContext stores the channel the staged batch goes out on.
// Returns {recipients, channel} like the GET, so the caller can apply the
// answer without a second read. The server refuses unknown channels
// (UNKNOWN_CHANNEL) — including 'auto' and 'email', which are not channels.
export async function setStagingAreaContext(context = {}) {
  const body = context.channel
    ? { dispatch_mode: 'direct_channel', direct_channel: context.channel === 'email' || context.channel === 'web' ? 'browser' : context.channel }
    : context;
  if (!body.dispatch_mode) throw new Error('A send method is required');
  const apiBase = getKernelApiBase();
  const resp = await fetch(`${apiBase}/staging-area/context`, {
    method: 'PUT',
    headers: orgHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    throw await lifecycleError(resp, `Could not set the staging channel (${resp.status})`);
  }
  const data = unwrapSamDbamResponse(await resp.json());
  return {
    recipients: Array.isArray(data?.recipients) ? data.recipients : [],
    dispatch_mode: data?.dispatch_mode || 'channel_response',
    deployment_id: data?.deployment_id || null,
    direct_channel: data?.direct_channel || null,
    direct_sender_binding_id: data?.direct_sender_binding_id || null,
    crm_offered_channels: Array.isArray(data?.crm_offered_channels) ? data.crm_offered_channels : [],
    crm_email_binding_id: data?.crm_email_binding_id || null,
    revision: Number(data?.revision) || 0,
    channel: data?.direct_channel || '',
  };
}

export async function previewDispatch() {
  const apiBase = getKernelApiBase();
  const resp = await fetch(`${apiBase}/dispatch/preview`, {
    method: 'POST', headers: orgHeaders({ 'Content-Type': 'application/json' }), body: '{}',
  });
  if (!resp.ok) throw await lifecycleError(resp, `Could not prepare the send review (${resp.status})`);
  return unwrapSamDbamResponse(await resp.json());
}

export async function listDispatchCapabilities() {
  const apiBase = getKernelApiBase();
  const resp = await fetch(`${apiBase}/dispatch/capabilities`, { headers: orgHeaders() });
  if (!resp.ok) throw await lifecycleError(resp, `Could not read channel capabilities (${resp.status})`);
  const data = unwrapSamDbamResponse(await resp.json());
  return Array.isArray(data) ? data : [];
}

export async function confirmDispatch({ previewHash, stagingRevision }) {
  const apiBase = getKernelApiBase();
  const resp = await fetch(`${apiBase}/dispatch/confirm`, {
    method: 'POST',
    headers: orgHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ preview_hash: previewHash, staging_revision: stagingRevision }),
  });
  if (!resp.ok) throw await lifecycleError(resp, `Could not confirm the dispatch (${resp.status})`);
  return unwrapSamDbamResponse(await resp.json());
}

export async function getDispatchBatch(dispatchBatchId) {
  const apiBase = getKernelApiBase();
  const resp = await fetch(`${apiBase}/dispatch/batches/${encodeURIComponent(dispatchBatchId)}`, { headers: orgHeaders() });
  if (!resp.ok) throw await lifecycleError(resp, `Could not read the dispatch (${resp.status})`);
  return unwrapSamDbamResponse(await resp.json());
}

export async function addStagingAreaRecipient({ globalRecipientId }) {
  if (!globalRecipientId) throw new Error('A recipient is required');
  return (await stagingAreaRequest({
    method: 'POST',
    body: { global_recipient_id: globalRecipientId },
  })).recipients;
}

export async function removeStagingAreaRecipient({ globalRecipientId }) {
  if (!globalRecipientId) throw new Error('A recipient is required');
  return (await stagingAreaRequest({
    method: 'DELETE',
    path: `/${encodeURIComponent(globalRecipientId)}`,
  })).recipients;
}

// ---- Plan limits ----------------------------------------------------------
//
// Read-only. Neither call enforces anything; enforcement happens in staging.
// These let the staging area show the author where they stand before anything
// is refused, and let the console close the door on the assistants for a
// used-up free account.

async function limitsGet(path) {
  const apiBase = getKernelApiBase();
  const resp = await fetch(`${apiBase}/limits/${path}`, { headers: orgHeaders() });
  if (!resp.ok) {
    throw await lifecycleError(resp, `Could not read your plan (${resp.status})`);
  }
  return unwrapSamDbamResponse(await resp.json());
}

// { tier, surveys_allowed, surveys_used, recipients_per_survey, resets_at }
// A null allowance means unlimited. A null resets_at means the count never
// resets, which today is only a free account.
export async function fetchPlanUsage() {
  return limitsGet('usage');
}

// { enabled, message? }. False only for a free account that has used up its
// surveys, because the assistants spend tokens and tokens cost money.
export async function fetchAssistantsEnabled() {
  return limitsGet('assistants');
}

// Undeploy: deployed → ready (silent) or sent → ready (completion-checked).
// Returns the UndeployResult. When `requires_confirmation` is true the
// deployment was NOT closed; repeat with confirmed:true to close.
export async function undeployInCloud({ deploymentId, confirmed = false }) {
  return lifecyclePost(deploymentId, 'undeploy', { confirmed: !!confirmed });
}

export async function listDeploymentsFromCloud({ orgId = '' } = {}) {
  const apiBase = getKernelApiBase();
  let url = `${apiBase}/deployments`;
  if (orgId) url += `?org_id=${encodeURIComponent(orgId)}`;
  const json = await httpRequestWithRetry(url, { method: 'GET' }, { timeoutMs: 10000 });
  return unwrapSamDbamResponse(json, true);
}

export async function listRecipientsFromCloud(deploymentId) {
  if (!deploymentId) return [];
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/deployments/${encodeURIComponent(deploymentId)}/users`;
  try {
    const json = await httpRequestWithRetry(url, { method: 'GET' }, { timeoutMs: 10000 });
    const items = unwrapSamDbamResponse(json, true);
    return items || [];
  } catch (e) {
    if (e.status === 404) return [];
    console.warn('listRecipientsFromCloud failed for', deploymentId, e.message);
    return [];
  }
}

export function getRecipientsCsvUrl(deploymentId) {
  const apiBase = getKernelApiBase();
  return `${apiBase}/recipients/${encodeURIComponent(deploymentId)}.csv`;
}

export function getResultsCsvUrl(deploymentId, format = 'long') {
  const apiBase = getKernelApiBase();
  const fmt = String(format).toLowerCase() === 'wide' ? '?format=wide' : '';
  return `${apiBase}/results/${encodeURIComponent(deploymentId)}.csv${fmt}`;
}

/**
 * Fetch Results JSON rows for a deployment (for Console Results grid)
 */
export async function fetchResultsJson(deploymentId, { timeoutMs = 8000 } = {}) {
  if (!deploymentId) throw new Error('deploymentId is required');
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/deployments/${encodeURIComponent(deploymentId)}/results`;
  const resp = await httpRequest(url, { method: 'GET' }, { timeoutMs, responseType: 'json' });
  return unwrapSamDbamResponse(resp, true);
}

/**
 * Fetch responses for a specific session (for SRA Results by session)
 */
export async function fetchSessionResponses(sessionId, { timeoutMs = 8000 } = {}) {
  if (!sessionId) throw new Error('sessionId is required');
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/sessions/${encodeURIComponent(sessionId)}/responses`;
  const resp = await httpRequest(url, { method: 'GET' }, { timeoutMs, responseType: 'json' });
  return unwrapSamDbamResponse(resp, true);
}

/**
 * Fetch Results CSV as raw text with timeout control.
 * Returns the CSV string. Caller is responsible for validation/downloading.
 */
export async function fetchResultsCsvText(deploymentId, { timeoutMs = 8000, format = 'long' } = {}) {
  if (!deploymentId) throw new Error('deploymentId is required');
  const url = getResultsCsvUrl(deploymentId, format);
  // Use generic HTTP helper to support timeout and proper error surfacing
  return await httpRequest(url, { method: 'GET' }, { timeoutMs, responseType: 'text' });
}

/**
 * Export results CSV with recipient filtering via POST (supports large selections)
 */
export async function exportResultsCsv(deploymentId, { recipientIds = null, format = 'long', timeoutMs = 20000 } = {}) {
  if (!deploymentId) throw new Error('deploymentId is required');
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/results/${encodeURIComponent(deploymentId)}/export`;

  const resp = await fetch(url, {
    method: 'POST',
    headers: orgHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ recipientIds, format }),
    signal: AbortSignal.timeout(timeoutMs)
  });

  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Export failed (${resp.status}): ${text || resp.statusText}`);
  }

  return await resp.text(); // Return CSV text
}


export async function addRecipientInCloud({ deploymentId, email, name }) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/recipients`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: orgHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ deployment_id: deploymentId, email, name })
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Add recipient failed (${resp.status}): ${text || resp.statusText}`);
  }
  return unwrapSamDbamResponse(await resp.json());
}

export async function removeRecipientLinkInCloud({ deploymentId, recipientId }) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/recipients/${encodeURIComponent(recipientId)}?deployment_id=${encodeURIComponent(deploymentId)}`;
  const resp = await fetch(url, { method: 'DELETE', headers: orgHeaders() });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Remove link failed (${resp.status}): ${text || resp.statusText}`);
  }
  return unwrapSamDbamResponse(await resp.json());
}

export async function removeRecipientLinksBulkInCloud({ deploymentId, recipientIds }) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/recipients:delete`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: orgHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ deployment_id: deploymentId, recipient_ids: recipientIds })
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Bulk remove links failed (${resp.status}): ${text || resp.statusText}`);
  }
  return unwrapSamDbamResponse(await resp.json());
}

export async function deleteSurveyInCloud({ surveyId }) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/surveys/${encodeURIComponent(surveyId)}`;
  const resp = await fetch(url, { method: 'DELETE', headers: orgHeaders() });
  // Idempotent delete: a 404 means the survey is already gone, which is the
  // exact end state a delete is asking for. Treat it as success (no-op) rather
  // than an error, so deleting an already-removed/archived survey doesn't fail.
  if (resp.status === 404) {
    return { ok: true, alreadyGone: true };
  }
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Delete survey failed (${resp.status}): ${text || resp.statusText}`);
  }
  // sam-dbam returns 204 No Content — no body to parse
  return { ok: true };
}

export async function copySurveyInCloud({ surveyId }) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/surveys/${encodeURIComponent(surveyId)}/clone`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: orgHeaders({ 'Content-Type': 'application/json' })
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Copy survey failed (${resp.status}): ${text || resp.statusText}`);
  }
  const data = await resp.json();
  return data.data || data;
}

export async function deleteSurveyVersionInCloud({ surveyId, versionNumber }) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/surveys/${encodeURIComponent(surveyId)}/versions/${encodeURIComponent(versionNumber)}`;
  const resp = await fetch(url, { method: 'DELETE', headers: orgHeaders() });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Delete version failed (${resp.status}): ${text || resp.statusText}`);
  }
  // HTTP 204 No Content on delete
  return { ok: true };
}

export async function updateSurveyInCloud({ surveyId, updates }) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/surveys/${encodeURIComponent(surveyId)}`;
  const resp = await fetch(url, {
    method: 'PATCH',
    headers: orgHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(updates || {})
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    if (resp.status === 404) throw new Error('Survey not found');
    throw new Error(`Survey update failed (${resp.status}): ${text || resp.statusText}`);
  }
  return resp.json();
}


// --- Users directory ---
export async function listUsersFromCloud({ limit = 50, offset = 0, search = '' } = {}) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/users?limit=${encodeURIComponent(limit)}&offset=${encodeURIComponent(offset)}&search=${encodeURIComponent(search)}`;
  const resp = await fetch(url, { headers: orgHeaders() });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`List users failed (${resp.status}): ${text || resp.statusText}`);
  }
  return resp.json();
}

export async function createUserInCloud({ email, name }) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/users`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: orgHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ email, name })
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Create user failed (${resp.status}): ${text || resp.statusText}`);
  }
  return resp.json();
}

export async function deleteUserInCloud({ userId }) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/users/${encodeURIComponent(userId)}`;
  const resp = await fetch(url, { method: 'DELETE', headers: orgHeaders() });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Delete user failed (${resp.status}): ${text || resp.statusText}`);
  }
  return resp.json();
}

export async function listDeploymentUsersFromCloud(deploymentId) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/deployments/${encodeURIComponent(deploymentId)}/users`;
  const resp = await fetch(url, { headers: orgHeaders() });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`List deployment users failed (${resp.status}): ${text || resp.statusText}`);
  }
  const json = await resp.json();
  const items = unwrapSamDbamResponse(json, true);
  return { items: items || [] };
}

export async function listDeploymentRecipientHistory(deploymentId) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/deployments/${encodeURIComponent(deploymentId)}/recipient-history?perPage=200`;
  const resp = await fetch(url, { headers: orgHeaders() });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`List recipient history failed (${resp.status}): ${text || resp.statusText}`);
  }
  const json = await resp.json();
  const inner = json?.data;  // unwrap outer {data: ..., meta: ...}
  const rows = Array.isArray(inner?.data) ? inner.data : Array.isArray(inner) ? inner : [];
  const pagination = inner?.pagination || json?.pagination || {};
  return { data: rows, pagination };
}

export async function linkUserToDeploymentInCloud({ deploymentId, userId, email, name }) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/deployments/${encodeURIComponent(deploymentId)}/users`;
  const resp = await fetch(url, {
    method: 'POST',
    headers: orgHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ user_id: userId, email: email || '', name: name || '' })
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Link user failed (${resp.status}): ${text || resp.statusText}`);
  }
  return resp.json();
}

export async function revokeUserLinkInCloud({ deploymentId, userId }) {
  const apiBase = getKernelApiBase();
  const url = `${apiBase}/deployments/${encodeURIComponent(deploymentId)}/users/${encodeURIComponent(userId)}`;
  const resp = await fetch(url, { method: 'DELETE', headers: orgHeaders() });
  if (!resp.ok) {
    const text = await resp.text().catch(() => '');
    throw new Error(`Revoke link failed (${resp.status}): ${text || resp.statusText}`);
  }
  return resp.json();
}

// --- Audio capture playback (kernel proxy) ---

/**
 * Get the kernel API base URL.
 * Prefers REACT_APP_KERNEL_URL if set, otherwise derives from REACT_APP_SAM_DBAM_URL
 * by replacing 'sam-dbam' with 'cloudsam-kernel' in the host.
 */
function getKernelUrl() {
  const explicit = process.env.REACT_APP_KERNEL_URL;
  if (explicit) return explicit.replace(/\/+$/, '');
  // The build already supplies the kernel host as REACT_APP_KERNEL_API_BASE
  // (deploy_all.sh passes _KERNEL_API_BASE=${KERNEL_URL}/api); accept it here so
  // audio doesn't depend on a second, never-set variable. Strip the /api suffix —
  // the audio callers below append the full /api/v5/audio path themselves.
  const apiBase = process.env.REACT_APP_KERNEL_API_BASE;
  if (apiBase) return apiBase.replace(/\/+$/, '').replace(/\/api$/, '');
  // Derive from sam-dbam URL: https://sam-dbam-XXX.run.app → https://cloudsam-kernel-XXX.run.app
  const dbamUrl = process.env.REACT_APP_SAM_DBAM_URL || '';
  const stripped = dbamUrl.replace(/\/dbam\/v1\/?$/, '').replace(/\/+$/, '');
  const derived = stripped.replace(/sam-dbam/, 'cloudsam-kernel');
  // The rewrite is a no-op against a custom domain (api.samsurveyor.com → sam-dbam),
  // which would send audio requests to sam-dbam and return a bare 404 that reads as
  // "recording unavailable". Fail loudly instead of quietly hitting the wrong service.
  if (!stripped || derived === stripped) return '';
  return derived;
}

/**
 * Fetch a short-lived signed URL for an audio recording stored in GCS.
 * @param {string} gcsPath - The gs:// path from response.audio_storage_path
 * @returns {Promise<string>} A signed URL for browser playback
 */
export async function getAudioSignedUrl(gcsPath) {
  const kernelBase = getKernelUrl();
  if (!kernelBase) throw new Error('Audio playback is unavailable: REACT_APP_KERNEL_URL is not configured');
  const url = `${kernelBase}/api/v5/audio/signed-url?gcsPath=${encodeURIComponent(gcsPath)}`;
  const resp = await httpRequest(url, { method: 'GET' }, { timeoutMs: 8000, responseType: 'json' });
  if (!resp || !resp.url) throw new Error('No signed URL returned');
  return resp.url;
}

/**
 * Delete an audio recording from GCS.
 * @param {string} gcsPath - The gs:// path to delete
 * @returns {Promise<{success: boolean}>}
 */
export async function deleteAudioRecording(gcsPath) {
  const kernelBase = getKernelUrl();
  if (!kernelBase) throw new Error('Audio deletion is unavailable: REACT_APP_KERNEL_URL is not configured');
  const url = `${kernelBase}/api/v5/audio`;
  return httpRequest(url, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ gcsPath })
  }, { timeoutMs: 8000, responseType: 'json' });
}

// ---- File Upload APIs ----

/**
 * Fetch file uploads for a session.
 * @param {string} sessionId - The kernel session ID
 * @returns {Promise<Array>} Array of file upload records
 */
export async function getFileUploads(sessionId) {
  const base = getKernelApiBase();
  const url = `${base}/file-uploads?session_id=${encodeURIComponent(sessionId)}`;
  const resp = await httpRequest(url, { method: 'GET' }, { timeoutMs: 8000, responseType: 'json' });
  // Backend wraps as {data: {files: [...]}, meta: {...}}
  const inner = (resp && resp.data) || {};
  return Array.isArray(inner) ? inner : (inner.files || []);
}

/**
 * Fetch file uploads for multiple sessions (bulk).
 * @param {string[]} sessionIds - Array of kernel session IDs
 * @returns {Promise<Object>} Map of session_id → file upload records
 */
export async function getFileUploadsBulk(sessionIds) {
  const base = getKernelApiBase();
  const results = {};
  // Fetch in parallel, max 10 concurrent
  const batches = [];
  for (let i = 0; i < sessionIds.length; i += 10) {
    batches.push(sessionIds.slice(i, i + 10));
  }
  for (const batch of batches) {
    const promises = batch.map(async (sid) => {
      try {
        const files = await getFileUploads(sid);
        results[sid] = files;
      } catch (_) {
        results[sid] = [];
      }
    });
    await Promise.all(promises);
  }
  return results;
}

/**
 * Get a signed URL for a single file upload.
 * @param {string} gcsPath - The gs:// path from file_upload record
 * @param {string} [filename] - Original filename (used for Content-Disposition)
 * @returns {Promise<string>} A signed download URL
 */
export async function getFileSignedUrl(gcsPath, filename) {
  const base = getKernelApiBase();
  let url = `${base}/file-uploads/signed-url?gcs_path=${encodeURIComponent(gcsPath)}`;
  if (filename) url += `&filename=${encodeURIComponent(filename)}`;
  const resp = await httpRequest(url, { method: 'GET' }, { timeoutMs: 8000, responseType: 'json' });
  // writeJSON wraps as {data: {url: "..."}, meta: {...}}
  const inner = (resp && resp.data) || resp || {};
  const signedUrl = inner.url || resp.url;
  if (!signedUrl) throw new Error('No signed URL returned');
  return signedUrl;
}

/**
 * Get signed URLs for multiple files (bulk download prep).
 * @param {string[]} gcsPaths - Array of gs:// paths
 * @returns {Promise<Array<{gcs_path: string, url: string, filename: string}>>}
 */
export async function getBulkFileUrls(gcsPaths) {
  const base = getKernelApiBase();
  const url = `${base}/file-uploads/bulk-signed-urls`;
  const resp = await httpRequest(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ gcs_paths: gcsPaths })
  }, { timeoutMs: 15000, responseType: 'json' });
  // writeJSON wraps as {data: {files: [...]}, meta: {...}}
  const inner = (resp && resp.data) || resp || {};
  return inner.files || inner.urls || [];
}
